import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';

import { CompanyCodeToNameMap } from '@shared-constants/company-codes.const';
import { ONLY_DIGITS_PATTERN } from '@shared-constants/pattern-constants';
import { IHttpResponseSearchableEmployee } from '@shared-interfaces/api';
import { IHttpErrorResponseObject } from '@shared-interfaces/error';

import { Employee } from '../../classes/employee/employee';
import { ISapRequestFilterQueryProps } from '../../models/api.model';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';

@Component({
  selector: 'crew-edit',
  templateUrl: './crew-edit.component.html',
  standalone: false,
})
export class CrewEditComponent implements OnInit {
  public filter: FormControl<string> = this.datahub.fb.control('');
  public shop: IHttpResponseSearchableEmployee[] = [];
  public crew: Array<Employee> = this.datahub.timesheet.currentEmployeeCrew;
  public readonly employeeSearch = new FormControl();
  public errorMessage = '';
  public inputText = '';
  public isSearchingEmployees = true;
  public allEmployees: IHttpResponseSearchableEmployee[] = [];
  public sameCostCenter: IHttpResponseSearchableEmployee[] = [];
  public differentCostCenter: IHttpResponseSearchableEmployee[] = [];
  public outsideCompanyCode: IHttpResponseSearchableEmployee[] = [];
  public readonly costCenterControl: FormControl<string> = this.datahub.costCenter.getCostCenterFormControl();
  public activeSearchCostCenter = this.datahub.extDocument.Costcenter1;
  public timesheetCompanyCode = this.datahub.contract.value.CompanyCode;
  public isMoreThan3Chars = false;
  private readonly searchSubject$ = new Subject<string>();

  constructor(
    private readonly destroyRef: DestroyRef,
    public readonly datahub: DatahubService, //TODO: The HTML template should not have access to the datahub! This needs to be private
    private readonly apiService: ApiService,
  ) {}

  public ngOnInit(): void {
    this.datahub.costCenter.shouldMissingCostCenterThrowError = false;
    this.searchSubject$.pipe(debounceTime(750), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isMoreThan3Chars = this.inputText.length < 3 && this.inputText.length !== 0;
      this.search();
    });

    if (!this.datahub.timesheet.isTimesheetInProgress) {
      this.datahub.resetApplicationState();
      return;
    }
    this.search();
  }

  public onSearch(): void {
    this.resetSearch();
    this.searchSubject$.next(this.inputText);
  }

  public launchOutsideCompanyCodeModal() {
    this.datahub
      .launchResponsiveModal('simple-display', {
        titleText: 'You cannot add this employee to your timesheet.',
        bodyText: `ONLY ${this.getCompanyCodeDescription(this.datahub.contract.value.CompanyCode)} employees can be added to this timesheet. Please contact your supervisor for help.`,
      })
      .afterClosed()
      .subscribe({
        error: (err) => {
          console.error('Error when closing the "outside company code" modal', err);
        },
      });
  }

  public search(): void {
    let queryParams: ISapRequestFilterQueryProps;

    if (!this.inputText && !this.costCenterControl.value) {
      this.isSearchingEmployees = false;
      this.errorMessage = 'Please enter a name or cost center to continue';
      return;
    }

    if (this.inputText) {
      if (this.inputText.length <= 2) {
        this.isSearchingEmployees = false;
      } else {
        queryParams = this.parseSearchValuesForQuery();
        this.getEmployeesForSearch(queryParams, true);
      }
    } else {
      queryParams = {
        CostCenter: this.costCenterControl.value,
        WeDate: this.datahub.date.getFilterString(),
      };
      this.getEmployeesForSearch(queryParams);
    }
  }

  private getEmployeesForSearch(queryParams: ISapRequestFilterQueryProps, shouldSearchAllCostCenters = false): void {
    const inputBeforeSearch = this.inputText;
    let inputAfterSearch: string;

    this.apiService.getEmployeesForSearch$(queryParams, shouldSearchAllCostCenters).subscribe({
      next: (employees: IHttpResponseSearchableEmployee[]) => {
        this.allEmployees = employees;
        if (this.allEmployees.length === 0) {
          this.isSearchingEmployees = false;
          this.errorMessage = `No Matches Found. \n\nPlease double check the search, or contact your supervisor for help.`;
        } else {
          inputAfterSearch = this.inputText;
          if (inputBeforeSearch === inputAfterSearch) {
            this.filterShop();
          } else {
            this.isSearchingEmployees = true;
            this.onSearch();
          }
        }
      },
      error: (err: IHttpErrorResponseObject) => {
        this.errorMessage =
          'An error occurred while fetching employees. \n\nIf this issue persists please contact your supervisor for help';
        console.error('An error occurred while fetching employees', err);
      },
    });
  }

  public back(): void {
    this.datahub.timesheet.navigateToSelf();
  }

  public add(emp: IHttpResponseSearchableEmployee | null, arrayName = 'sameCostCenter'): void {
    if (!emp) return;
    if (this.datahub.timesheet.currentEmployeeCrew.some((x) => x.EmpId === emp.EmpId)) {
      this.datahub.messageService.info(`${emp.EmpId} - ${emp.FirstName} ${emp.LastName} has already been added.`);
      return;
    }

    switch (arrayName) {
      case 'differentCostCenter':
        this.differentCostCenter = this.differentCostCenter.filter((x) => x !== emp);
        break;
      case 'sameCostCenter':
        this.sameCostCenter = this.sameCostCenter.filter((x) => x !== emp);
        break;
      default:
        console.error('arrayName does not exist');
    }

    const allEmployees = this.sameCostCenter.concat(this.differentCostCenter, this.outsideCompanyCode);
    if (allEmployees.length === 0) {
      this.errorMessage = `No Employees Remain in Cost Center ${this.datahub.costCenter.getCostCenterValue()}`;
    }
    const pretender: IHttpResponseEmployeeAndUi = {
      DAWeek: '',
      EmpToActivity: { results: [] },
      InvalidDates: '',
      dailyTotal: '0',
      weeklyTotal: '0',
      records: [],
      display: `${emp.EmpId} - ${emp.LastName}, ${emp.FirstName}`,
      isPending: true,
      ...emp,
    };

    this.datahub.timesheet.addEmployee(pretender);

    const documentNum = this.datahub.extDocument.Documentno;
    const date = this.datahub.date.getSapDateString();
    const userEmployeeNum = this.datahub.userService.getEmployeeNumber();
    const employeeNum = pretender.EmpId;

    this.apiService.getOneEmployee$(documentNum, date, userEmployeeNum, employeeNum).subscribe({
      next: (employee) => {
        const indexOfEmployeeToChange = this.datahub.timesheet.currentEmployeeCrew.findIndex((x) => x.EmpId === employee.EmpId);
        if (indexOfEmployeeToChange > -1) {
          this.datahub.timesheet.currentEmployeeCrew[indexOfEmployeeToChange] = new Employee(employee, this.datahub);
        }
      },
      error: (err: IHttpErrorResponseObject) => {
        this.errorMessage =
          'An error occurred while fetching employees. \n\nIf this issue persists please contact your supervisor for help';
        console.error('An error occurred while fetching employees', err);
      },
    });
  }

  public remove(emp: Employee): void {
    this.errorMessage = '';
    emp.removeSelf(this.crew);
    this.returnToProperArray(emp);
  }

  public clearAll(): void {
    this.errorMessage = '';
    this.datahub.timesheet.currentEmployeeCrew.forEach((employee: Employee) => {
      this.returnToProperArray(employee);
    });
    this.datahub.timesheet.clearAllEmployees();
  }

  private returnToProperArray(emp: Employee): void {
    this.activeSearchCostCenter = this.costCenterControl.value
      ? this.costCenterControl.value
      : this.datahub.extDocument.Costcenter1;
    if (emp.CompanyCode === this.timesheetCompanyCode && emp.CostCenter === this.activeSearchCostCenter) {
      this.sameCostCenter.push(emp);
      this.sameCostCenter = this.sortByCCandLastName(this.sameCostCenter);
    } else if (emp.CostCenter !== this.activeSearchCostCenter && emp.CompanyCode === this.timesheetCompanyCode) {
      this.differentCostCenter.push(emp);
      this.differentCostCenter = this.sortByCCandLastName(this.differentCostCenter);
    } else {
      this.outsideCompanyCode.push(emp);
      this.outsideCompanyCode = this.sortByCCandLastName(this.outsideCompanyCode);
    }
  }

  private parseSearchValuesForQuery(): ISapRequestFilterQueryProps {
    if (this.inputText && ONLY_DIGITS_PATTERN.test(this.inputText)) {
      const empIdSearchQuery = {
        WeDate: this.datahub.date.getFilterString(),
        EmpId: this.inputText,
      };
      return empIdSearchQuery;
    } else {
      // SAP does not like single apostrophes in the search query, so we replace a single apostrophe with double apostrophes
      const firstLastName = this.inputText.replace(/'/g, "''").split(' ');
      const firstName = firstLastName[0];
      const lastName = firstLastName[1];

      const nameSearchQuery = {
        WeDate: this.datahub.date.getFilterString(),
        SortName: (lastName ? `${lastName}* ` : '*') + (firstName ? `${firstName}*` : '*'),
      };
      return nameSearchQuery;
    }
  }

  private filterShop(): void {
    this.errorMessage = '';
    this.datahub.timesheet.currentEmployeeCrew.forEach((employee: Employee) => {
      employee.removeSelf(this.allEmployees);
    });
    this.allEmployees = this.sortByCCandLastName(this.allEmployees);
    this.separateShop();
    this.isSearchingEmployees = false;
  }

  private sortByCCandLastName(arrayToBeSorted: IHttpResponseSearchableEmployee[]): IHttpResponseSearchableEmployee[] {
    return arrayToBeSorted.sort((x, y) => Number(x.CostCenter) - Number(y.CostCenter) || x.LastName.localeCompare(y.LastName));
  }

  private separateShop(): void {
    this.activeSearchCostCenter = this.costCenterControl.value
      ? this.costCenterControl.value
      : this.datahub.extDocument.Costcenter1;
    this.allEmployees.map((employee) => {
      if (employee.CompanyCode === this.timesheetCompanyCode && employee.CostCenter === this.activeSearchCostCenter) {
        this.sameCostCenter.push(employee);
      } else if (employee.CostCenter !== this.activeSearchCostCenter && employee.CompanyCode === this.timesheetCompanyCode) {
        this.differentCostCenter.push(employee);
      } else {
        this.outsideCompanyCode.push(employee);
      }
    });
  }

  private resetSearch(): void {
    this.allEmployees = [];
    this.sameCostCenter = [];
    this.differentCostCenter = [];
    this.outsideCompanyCode = [];
    this.isSearchingEmployees = true;
    this.errorMessage = '';
  }

  private getCompanyCodeDescription(companyCode: string): string {
    const foundCode = CompanyCodeToNameMap[companyCode];
    return foundCode ?? 'Unknown';
  }
}
interface IHttpResponseEmployeeAndUi extends IHttpResponseSearchableEmployee {
  weeklyTotal: string;
  dailyTotal: string;
  records: [];
  display: string;
  isPending: boolean;
  DAWeek: string;
  EmpToActivity: { results: [] };
  InvalidDates: string;
}
